import React, { useState, useEffect } from 'react';
import './CareerForm.css';

import { Heading, PageTitle } from '../../../components';

import { FaUser, FaEnvelope, FaBriefcase, FaCommentDots, FaAngleDown, FaCertificate, FaPhoneAlt } from 'react-icons/fa';
import { FaLocationDot } from 'react-icons/fa6';

import axios from "axios";

const API_PATH = 'https://acme-react.asdesignsgalaxy.com/api/phpmailer/career.php';

const EMAIL_REGEX = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
const PHONE_REGEX = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;



const CareerForm = () => {

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    number: '',
    position: '',
    location: '',
    skills: '',
    experience: '',
    portfolio: '',
    message: ''
  });

  const [mailSent, setMailSent] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (mailSent) {
      const timer = setTimeout(() => setMailSent(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [mailSent]);

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const validationErrors = [];

      if (!formData.name.trim()) validationErrors.push('Full name is required');
      if (!formData.email.trim()) {
        validationErrors.push('Email is required');
      } else if (!EMAIL_REGEX.test(formData.email)) {
        validationErrors.push('Invalid email format');
      }
      if (!formData.number.trim()) {
        validationErrors.push('Phone number is required');
      } else if (!PHONE_REGEX.test(formData.number)) {
        validationErrors.push('Invalid phone number format');
      }
      if (!formData.position) validationErrors.push('Position is required');
      if (!formData.location.trim()) validationErrors.push('Location is required');
      if (!formData.skills.trim()) validationErrors.push('Skills are required');
      if (!formData.experience || formData.experience < 0) validationErrors.push('Valid years of experience is required');
      if (!formData.message.trim()) validationErrors.push('Cover letter is required');

      if (validationErrors.length > 0) {
        throw new Error(validationErrors.join(' | '));
      }

      const postData = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        postData.append(key, value);
      });

      const response = await axios.post(API_PATH, postData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (!response.data.sent) {
        throw new Error(response.data.message || 'Application submission failed');
      }

      setMailSent(true);
      setFormData({
        name: '',
        email: '',
        number: '',
        position: '',
        location: '',
        skills: '',
        experience: '',
        portfolio: '',
        message: ''
      });
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <>
      <PageTitle title={'Join Our Print Team'} page={'Career Form'} />

      <section className="custom-form career-form">
        <div className="box-container">
          <div className="content">
            <form className="dark-form" onSubmit={handleSubmitForm}>
              <Heading 
                title={"Print Shop Career Opportunities"} 
                subTitle={"Become Part Team"} 
                linear 
                sideHeading 
              />

              <div className="input-box">
                <div className="input-field">
                  <label htmlFor="name"><FaUser /></label>
                  <input
                    type="text"
                    name="name"
                    className="box"
                    id="name"
                    placeholder="Your Full Name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  />
                </div>

                <div className="input-field">
                  <label htmlFor="email"><FaEnvelope /></label>
                  <input
                    type="email"
                    name="email"
                    className="box"
                    id="email"
                    placeholder="Your Email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              <div className="input-box">
                <div className="input-field">
                  <label htmlFor="number"><FaPhoneAlt /></label>
                  <input
                    type="number"
                    name="number"
                    className="box"
                    id="number"
                    placeholder="Your Phone Number"
                    value={formData.number}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  />
                </div>

                <div className="select">
                  <div className="select-container">
                    <label htmlFor="position"><FaBriefcase /></label>
                    <select
                      className="select-box"
                      id="position"
                      name="position"
                      value={formData.position}
                      onChange={handleInputChange}
                      required
                      disabled={isSubmitting}
                    >
                      <option value="">Select Position</option>
                      <option value="Print Operator">Print Operator</option>
                      <option value="Graphic Designer">Graphic Designer</option>
                      <option value="Prepress Technician">Prepress Technician</option>
                      <option value="Bindery Operator">Bindery Operator</option>
                      <option value="Customer Service">Customer Service</option>
                      <option value="Sales Representative">Sales Representative</option>
                      <option value="Other">Other</option>
                    </select>
                    <div className="icon-container"><FaAngleDown className="icon" /></div>
                  </div>
                </div>
              </div>

              <div className="input-box">
                <div className="input-field">
                  <label htmlFor="location"><FaLocationDot /></label>
                  <input
                    type="text"
                    name="location"
                    className="box"
                    id="location"
                    placeholder="Your City/State"
                    value={formData.location}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              <div className="input-box">
                <div className="input-field">
                  <label htmlFor="skills"><FaCertificate /></label>
                  <input
                    type="text"
                    name="skills"
                    className="box"
                    id="skills"
                    placeholder="Relevant Skills (e.g., Adobe Suite, Press Operation)"
                    value={formData.skills}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  />
                </div>

                <div className="input-field">
                  <label htmlFor="experience"><FaBriefcase /></label>
                  <input
                    type="number"
                    name="experience"
                    className="box"
                    id="experience"
                    placeholder="Years of Print Industry Experience"
                    value={formData.experience}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                    min="0"
                  />
                </div>

              </div>

              <div className='input-box'>
                <div className="input-field">
                  <label htmlFor="portfolio"><FaBriefcase /></label>
                  <input
                    type="url"
                    name="portfolio"
                    className="box"
                    id="portfolio"
                    placeholder="Portfolio Link"
                    value={formData.portfolio}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              <div className="input-field text-area">
                <label htmlFor="message"><FaCommentDots /></label>
                <textarea
                  cols="30"
                  rows="6"
                  name="message"
                  className="box"
                  id="message"
                  placeholder="Tell us about your print industry experience and why you'd be a great fit"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                ></textarea>
              </div>

              <button 
                type="submit" 
                className="btn ink-btn" 
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Apply Now'}
              </button>

              {error && (
                <div className="alert error">
                  {error.split(' | ').map((msg, i) => (
                    <span key={i}>{msg}</span>
                  ))}
                </div>
              )}

              {mailSent && <div className="alert success"><span>Application Submitted Successfully!</span></div>}
            </form>
          </div>
        </div>
      </section>
    </>
  );
}

export default CareerForm;